import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, onEnquire, onWishlist, onQuickView }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleImageRotation = () => {
    if (product?.images && product?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product?.images?.length);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(price);
  };

  return (
    <motion.div
      className="group bg-card border border-border rounded-xl overflow-hidden liquid-shadow hover:shadow-xl brand-transition"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-muted to-background">
        <Image
          src={product?.images ? product?.images?.[currentImageIndex] : product?.image}
          alt={product?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-110 brand-transition duration-700"
          onMouseEnter={handleImageRotation}
        />
        
        {/* Overlay Actions */}
        <div className={`absolute inset-0 bg-black/20 flex items-center justify-center space-x-3 brand-transition ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => onQuickView(product)}
            className="bg-white/90 hover:bg-white text-text-primary"
          >
            <Icon name="Eye" size={18} />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => onWishlist(product)}
            className="bg-white/90 hover:bg-white text-text-primary"
          >
            <Icon name={product?.isWishlisted ? "Heart" : "Heart"} size={18} className={product?.isWishlisted ? "fill-red-500 text-red-500" : ""} />
          </Button>
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col space-y-2">
          {product?.isNew && (
            <span className="px-2 py-1 bg-accent text-white text-xs font-inter font-medium rounded-full">
              New
            </span>
          )}
          {product?.isLimited && (
            <span className="px-2 py-1 bg-warning text-white text-xs font-inter font-medium rounded-full">
              Limited
            </span>
          )}
          {product?.isBestseller && (
            <span className="px-2 py-1 bg-success text-white text-xs font-inter font-medium rounded-full">
              Bestseller
            </span>
          )}
        </div>

        {/* Sustainability Badge */}
        {product?.sustainabilityScore && (
          <div className="absolute top-3 right-3">
            <div className="flex items-center space-x-1 px-2 py-1 bg-trust/90 text-white rounded-full">
              <Icon name="Leaf" size={12} />
              <span className="text-xs font-inter font-medium">{product?.sustainabilityScore}%</span>
            </div>
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4">
        {/* Category & Rating */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-inter font-medium text-primary uppercase tracking-wide">
            {product?.category}
          </span>
          {product?.rating && (
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={14} className="text-warning fill-warning" />
              <span className="text-sm font-inter text-text-secondary">
                {product?.rating} ({product?.reviewCount})
              </span>
            </div>
          )}
        </div>

        {/* Product Name */}
        <h3 className="text-lg font-poppins font-semibold text-text-primary mb-2 line-clamp-2 group-hover:text-primary brand-transition">
          {product?.name}
        </h3>

        {/* Features */}
        <div className="flex flex-wrap gap-1 mb-3">
          {product?.features?.slice(0, 3)?.map((feature, index) => (
            <span 
              key={index}
              className="px-2 py-1 bg-muted text-text-secondary text-xs font-inter rounded-md"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Specifications */}
        <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Droplets" size={14} className="text-accent" />
            <span className="text-text-secondary font-inter">{product?.capacity}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Thermometer" size={14} className="text-accent" />
            <span className="text-text-secondary font-inter">{product?.insulation}</span>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-poppins font-bold text-text-primary">
              {formatPrice(product?.price)}
            </span>
            {product?.originalPrice && product?.originalPrice > product?.price && (
              <span className="text-sm font-inter text-text-secondary line-through">
                {formatPrice(product?.originalPrice)}
              </span>
            )}
          </div>
          {product?.originalPrice && product?.originalPrice > product?.price && (
            <span className="px-2 py-1 bg-success/10 text-success text-xs font-inter font-medium rounded-full">
              {Math.round(((product?.originalPrice - product?.price) / product?.originalPrice) * 100)}% OFF
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onQuickView(product)}
            className="flex-1"
          >
            <Icon name="Eye" size={16} className="mr-2" />
            Quick View
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => onEnquire(product)}
            className="flex-1 bg-primary hover:bg-primary/90"
          >
            <Icon name="MessageCircle" size={16} className="mr-2" />
            Enquire
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;